import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatTRY } from '@/lib/currencyFormat';
import Badge from '@/components/base/Badge';
import { paymentStatus, deliveryStatus, installmentStatus } from '@/lib/statusDefs';

interface CustomerSalesTabProps {
  sales: any[];
  saleItemsMap: Record<string, any[]>;
  installmentsBySale: Record<string, any[]>;
  deliveries: any[];
  promissoryNotes?: any[];
  customerId?: string;
}

export default function CustomerSalesTab({ sales, saleItemsMap, installmentsBySale, deliveries, promissoryNotes = [], customerId }: CustomerSalesTabProps) {
  const navigate = useNavigate();
  const [expandedSaleId, setExpandedSaleId] = useState<string | null>(null);
  const [expandedMobileId, setExpandedMobileId] = useState<string | null>(null);

  const noteNoMap: Record<string, string> = {};
  promissoryNotes.forEach((n: any) => { if (n.id) noteNoMap[n.id] = n.note_no; });

  const toggleExpand = (saleId: string) => {
    setExpandedSaleId(prev => prev === saleId ? null : saleId);
  };

  const toggleMobile = (saleId: string) => {
    setExpandedMobileId(prev => prev === saleId ? null : saleId);
  };

  if (sales.length === 0) {
    return (
      <div className="p-5">
        <div className="flex flex-col items-center py-8 gap-2">
          <div className="w-12 h-12 rounded-2xl bg-background-100 flex items-center justify-center">
            <i className="ri-shopping-cart-2-line text-xl text-foreground-300"></i>
          </div>
          <p className="text-sm font-medium text-foreground-500">Henüz satış kaydı yok</p>
          <button onClick={() => navigate(customerId ? `/sales/new?customerId=${customerId}` : '/sales/new')} className="btn-primary text-sm cursor-pointer whitespace-nowrap">
            Yeni Satış Ekle
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-5">
      {/* ==================== DESKTOP TABLE ==================== */}
      <div className="hidden lg:block">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="border-b border-background-100">
                <th className="table-header w-10"></th>
                <th className="table-header">Satış No</th>
                <th className="table-header">Tarih</th>
                <th className="table-header">Kategori</th>
                <th className="table-header">Tutar</th>
                <th className="table-header">Ödenen</th>
                <th className="table-header">Kalan</th>
                <th className="table-header">Ödeme</th>
                <th className="table-header">Teslimat</th>
              </tr>
            </thead>
            <tbody>
              {sales.map(sale => {
                const isExpanded = expandedSaleId === sale.id;
                const items = saleItemsMap[sale.id] || [];
                const installments = installmentsBySale[sale.id] || [];
                const saleDelivery = deliveries.find((d: any) => d.sale_id === sale.id);
                const hasInstallments = installments.length > 0;

                return (
                  <tr key={sale.id} className={`border-b border-background-100 transition-colors ${isExpanded ? 'bg-background-50' : 'hover:bg-background-50'}`}>
                    <td className="table-cell py-2.5 pl-4">
                      <button
                        onClick={() => toggleExpand(sale.id)}
                        className="w-7 h-7 rounded-lg hover:bg-background-200 flex items-center justify-center cursor-pointer transition-transform duration-200"
                        style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                      >
                        <i className="ri-arrow-right-s-line text-foreground-400"></i>
                      </button>
                    </td>
                    <td className="table-cell py-2.5">
                      <div className="flex items-center gap-2">
                        <a
                          href={`/sales/${sale.id}`}
                          onClick={(e) => { e.stopPropagation(); }}
                          className="text-primary-600 font-medium hover:underline cursor-pointer whitespace-nowrap"
                        >
                          {sale.sale_no}
                        </a>
                        {sale.promissory_note_id && noteNoMap[sale.promissory_note_id] && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-600 border border-purple-200 font-medium whitespace-nowrap">
                            <i className="ri-link text-[9px] mr-0.5"></i>{noteNoMap[sale.promissory_note_id]}
                          </span>
                        )}
                        {sale.is_legacy && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium whitespace-nowrap">Devir</span>
                        )}
                      </div>
                    </td>
                    <td className="table-cell text-foreground-500 whitespace-nowrap">{sale.sale_date ? new Date(sale.sale_date).toLocaleDateString('tr-TR') : ''}</td>
                    <td className="table-cell text-foreground-400">{sale.product_category || '—'}</td>
                    <td className="table-cell font-medium">{formatTRY(sale.total_amount || 0)}</td>
                    <td className="table-cell text-emerald-600">{formatTRY(sale.paid_amount || 0)}</td>
                    <td className="table-cell">{(sale.remaining_amount || 0) > 0 ? <span className="text-red-500 font-medium">{formatTRY(sale.remaining_amount)}</span> : <span className="text-emerald-600">—</span>}</td>
                    <td className="table-cell py-2"><Badge badgeClass={paymentStatus.badgeClass(sale.payment_status)} icon={paymentStatus.icon(sale.payment_status)}>{sale.payment_status}</Badge></td>
                    <td className="table-cell py-2 pr-4"><Badge badgeClass={deliveryStatus.badgeClass(sale.delivery_status)} icon={deliveryStatus.icon(sale.delivery_status)}>{sale.delivery_status}</Badge></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Expanded detail panels */}
        {sales.map(sale => {
          if (expandedSaleId !== sale.id) return null;
          const items = saleItemsMap[sale.id] || [];
          const installments = installmentsBySale[sale.id] || [];
          const saleDelivery = deliveries.find((d: any) => d.sale_id === sale.id);

          return (
            <div key={`detail-${sale.id}`} className="bg-background-50 border-t border-background-200/70 px-5 py-4 animate-slide-up">
              {/* ---- Ürünler ---- */}
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-shopping-bag-3-line text-foreground-400 text-sm"></i>
                  <h4 className="text-sm font-semibold text-foreground-800">Satılan Ürünler</h4>
                  <span className="text-xs text-foreground-400">({items.length} ürün)</span>
                </div>
                {items.length > 0 ? (
                  <div className="overflow-x-auto rounded-lg border border-background-200/70">
                    <table className="w-full min-w-[900px] text-xs">
                      <thead>
                        <tr className="bg-background-100">
                          <th className="px-3 py-2 text-left text-foreground-500 font-medium">Ürün Cinsi</th>
                          <th className="px-3 py-2 text-left text-foreground-500 font-medium">Ürün Adı</th>
                          <th className="px-3 py-2 text-left text-foreground-500 font-medium">Marka</th>
                          <th className="px-3 py-2 text-left text-foreground-500 font-medium">Model Kod</th>
                          <th className="px-3 py-2 text-left text-foreground-500 font-medium">Renk</th>
                          <th className="px-3 py-2 text-left text-foreground-500 font-medium">Ölçü</th>
                          <th className="px-3 py-2 text-center text-foreground-500 font-medium">Adet</th>
                          <th className="px-3 py-2 text-right text-foreground-500 font-medium">Birim Fiyat</th>
                          <th className="px-3 py-2 text-right text-foreground-500 font-medium">Toplam</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item: any) => (
                          <tr key={item.id} className="border-t border-background-100">
                            <td className="px-3 py-2 text-foreground-700">{item.product_type || '—'}</td>
                            <td className="px-3 py-2 text-foreground-700 font-medium">{item.product_name || '—'}</td>
                            <td className="px-3 py-2 text-foreground-500">{item.brand || '—'}</td>
                            <td className="px-3 py-2 text-foreground-400 text-[11px]">{item.model_code || '—'}</td>
                            <td className="px-3 py-2 text-foreground-500">{item.color || '—'}</td>
                            <td className="px-3 py-2 text-foreground-500">{item.size || '—'}</td>
                            <td className="px-3 py-2 text-center text-foreground-700">{item.quantity}</td>
                            <td className="px-3 py-2 text-right text-foreground-600">{formatTRY(item.unit_price)}</td>
                            <td className="px-3 py-2 text-right font-medium text-foreground-800">{formatTRY(item.total_price)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-foreground-400 italic">Bu satış için ürün detayı kaydedilmemiş.</p>
                )}
              </div>

              {/* ---- Satış Bilgileri ---- */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="bg-background-100/70 rounded-lg p-3">
                  <span className="text-[10px] text-foreground-400 uppercase tracking-wider block">Satış Tarihi</span>
                  <span className="text-sm font-medium text-foreground-800 mt-0.5 block">{sale.sale_date ? new Date(sale.sale_date).toLocaleDateString('tr-TR') : '—'}</span>
                </div>
                <div className="bg-background-100/70 rounded-lg p-3">
                  <span className="text-[10px] text-foreground-400 uppercase tracking-wider block">Ödeme Tipi</span>
                  <span className="text-sm font-medium text-foreground-800 mt-0.5 block">{sale.payment_type || '—'}</span>
                </div>
                <div className="bg-background-100/70 rounded-lg p-3">
                  <span className="text-[10px] text-foreground-400 uppercase tracking-wider block">Peşinat</span>
                  <span className="text-sm font-medium text-foreground-800 mt-0.5 block">{formatTRY(sale.down_payment || 0)}</span>
                </div>
                <div className="bg-background-100/70 rounded-lg p-3">
                  <span className="text-[10px] text-foreground-400 uppercase tracking-wider block">Toplam Ödenen</span>
                  <span className="text-sm font-medium text-emerald-600 mt-0.5 block">{formatTRY(sale.paid_amount || 0)}</span>
                </div>
                <div className="bg-background-100/70 rounded-lg p-3">
                  <span className="text-[10px] text-foreground-400 uppercase tracking-wider block">Kalan Borç</span>
                  <span className={`text-sm font-medium mt-0.5 block ${(sale.remaining_amount || 0) > 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                    {(sale.remaining_amount || 0) > 0 ? formatTRY(sale.remaining_amount) : '—'}
                  </span>
                </div>
                <div className="bg-background-100/70 rounded-lg p-3">
                  <span className="text-[10px] text-foreground-400 uppercase tracking-wider block">Teslimat Durumu</span>
                  <Badge badgeClass={deliveryStatus.badgeClass(sale.delivery_status)} icon={deliveryStatus.icon(sale.delivery_status)}>{sale.delivery_status || '—'}</Badge>
                </div>
                {saleDelivery && (
                  <>
                    {saleDelivery.delivery_date && (
                      <div className="bg-background-100/70 rounded-lg p-3">
                        <span className="text-[10px] text-foreground-400 uppercase tracking-wider block">Teslimat Tarihi</span>
                        <span className="text-sm font-medium text-foreground-800 mt-0.5 block">{new Date(saleDelivery.delivery_date + 'T00:00:00').toLocaleDateString('tr-TR')}</span>
                      </div>
                    )}
                    {saleDelivery.delivery_person && (
                      <div className="bg-background-100/70 rounded-lg p-3">
                        <span className="text-[10px] text-foreground-400 uppercase tracking-wider block">Nakliyeci</span>
                        <span className="text-sm font-medium text-foreground-800 mt-0.5 block">
                          {saleDelivery.delivery_person}{saleDelivery.vehicle_plate ? ` (${saleDelivery.vehicle_plate})` : ''}
                        </span>
                      </div>
                    )}
                  </>
                )}
                {sale.warranty_period && (
                  <div className="bg-background-100/70 rounded-lg p-3">
                    <span className="text-[10px] text-foreground-400 uppercase tracking-wider block">Garanti</span>
                    <span className="text-sm font-medium text-foreground-800 mt-0.5 block">{sale.warranty_period}</span>
                  </div>
                )}
              </div>

              {/* ---- Satış Notu ---- */}
              {sale.notes && (
                <div className="mb-4 bg-background-100/70 rounded-lg p-3">
                  <span className="text-[10px] text-foreground-400 uppercase tracking-wider block mb-1">Satış Notu</span>
                  <p className="text-sm text-foreground-600">{sale.notes}</p>
                </div>
              )}

              {/* ---- Taksit Planı ---- */}
              {installments.length > 0 && (
                <div className="mb-2">
                  <div className="flex items-center gap-2 mb-3">
                    <i className="ri-calendar-schedule-line text-foreground-400 text-sm"></i>
                    <h4 className="text-sm font-semibold text-foreground-800">Ödeme Planı</h4>
                    {(installments[0]?.plan_type) && (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${installments[0].plan_type === 'custom' ? 'bg-accent-100 text-accent-700' : 'bg-background-200 text-foreground-500'}`}>
                        {installments[0].plan_type === 'custom' ? 'Özel Plan' : 'Eşit Taksit'}
                      </span>
                    )}
                    <span className="text-xs text-foreground-400 ml-auto">
                      Toplam: {formatTRY(installments.reduce((s: number, i: any) => s + (i.original_amount || i.amount || 0), 0))} ·
                      Ödenen: {formatTRY(installments.reduce((s: number, i: any) => s + (i.paid_amount || 0), 0))} ·
                      Kalan: {formatTRY(installments.reduce((s: number, i: any) => s + (i.remaining_amount !== undefined ? i.remaining_amount : ((i.amount || 0) - (i.paid_amount || 0))), 0))}
                    </span>
                  </div>
                  <div className="overflow-x-auto rounded-lg border border-background-200/70">
                    <table className="w-full min-w-[650px] text-xs">
                      <thead>
                        <tr className="bg-background-100">
                          <th className="px-3 py-2 text-left text-foreground-500 font-medium">Taksit No</th>
                          <th className="px-3 py-2 text-left text-foreground-500 font-medium">Vade Tarihi</th>
                          <th className="px-3 py-2 text-right text-foreground-500 font-medium">Tutar</th>
                          <th className="px-3 py-2 text-right text-foreground-500 font-medium">Ödenen</th>
                          <th className="px-3 py-2 text-right text-foreground-500 font-medium">Kalan</th>
                          <th className="px-3 py-2 text-center text-foreground-500 font-medium">Durum</th>
                        </tr>
                      </thead>
                      <tbody>
                        {installments.map((inst: any) => {
                          const instRemaining = inst.remaining_amount !== undefined ? inst.remaining_amount : ((inst.amount || 0) - (inst.paid_amount || 0));
                          const overdue = inst.status !== 'Ödendi' && inst.status !== 'İptal' && inst.due_date && inst.due_date < new Date().toISOString().split('T')[0] && instRemaining > 0;
                          return (
                            <tr key={inst.id} className={`border-t border-background-100 ${overdue ? 'bg-red-50/50' : ''}`}>
                              <td className="px-3 py-2 text-foreground-500">{inst.installment_no || inst.sequence_no || '—'}</td>
                              <td className="px-3 py-2 text-foreground-700">{inst.due_date ? new Date(inst.due_date).toLocaleDateString('tr-TR') : '—'}</td>
                              <td className="px-3 py-2 text-right font-medium text-foreground-800">{formatTRY(inst.original_amount || inst.amount)}</td>
                              <td className="px-3 py-2 text-right">{inst.paid_amount > 0 ? <span className="text-emerald-600">{formatTRY(inst.paid_amount)}</span> : '—'}</td>
                              <td className="px-3 py-2 text-right">{instRemaining > 0 ? <span className="text-red-500 font-medium">{formatTRY(instRemaining)}</span> : <span className="text-emerald-600">—</span>}</td>
                              <td className="px-3 py-2 text-center"><Badge badgeClass={installmentStatus.badgeClass(overdue ? 'Gecikti' : inst.status)} icon={installmentStatus.icon(overdue ? 'Gecikti' : inst.status)}>{overdue ? 'Gecikti' : inst.status}</Badge></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ---- Detay Sayfası Linki ---- */}
              <div className="flex justify-end mt-3 pt-3 border-t border-background-100">
                <button
                  onClick={() => navigate(`/sales/${sale.id}`)}
                  className="flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer transition-colors"
                >
                  <i className="ri-external-link-line"></i>
                  Satış Detayına Git
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ==================== MOBILE CARDS ==================== */}
      <div className="lg:hidden space-y-2.5">
        {sales.map(sale => {
          const isExpanded = expandedMobileId === sale.id;
          const items = saleItemsMap[sale.id] || [];
          const installments = installmentsBySale[sale.id] || [];
          const saleDelivery = deliveries.find((d: any) => d.sale_id === sale.id);

          return (
            <div key={sale.id} className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden transition-all">
              {/* Header Row */}
              <div
                className="p-3.5 flex items-center gap-3 cursor-pointer active:bg-background-100"
                onClick={() => toggleMobile(sale.id)}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-sm font-semibold text-foreground-800">{sale.sale_no}</span>
                    {sale.promissory_note_id && noteNoMap[sale.promissory_note_id] && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-600 border border-purple-200 font-medium whitespace-nowrap">
                        {noteNoMap[sale.promissory_note_id]}
                      </span>
                    )}
                    {sale.is_legacy && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Devir</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-foreground-400">{sale.sale_date ? new Date(sale.sale_date).toLocaleDateString('tr-TR') : ''}</span>
                    <span className="text-xs text-foreground-300">·</span>
                    <span className="text-xs text-foreground-500">{sale.product_category || 'Diğer'}</span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className="text-sm font-bold text-foreground-800 block">{formatTRY(sale.total_amount || 0)}</span>
                  {(sale.remaining_amount || 0) > 0 && (
                    <span className="text-xs text-red-500">Kalan: {formatTRY(sale.remaining_amount)}</span>
                  )}
                </div>
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'bg-background-200 rotate-90' : ''}`}>
                  <i className="ri-arrow-right-s-line text-foreground-400"></i>
                </div>
              </div>

              {/* Badge Row */}
              <div className="px-3.5 pb-2 flex items-center gap-1.5 flex-wrap">
                <Badge badgeClass={paymentStatus.badgeClass(sale.payment_status)} icon={paymentStatus.icon(sale.payment_status)}>{sale.payment_status}</Badge>
                <Badge badgeClass={deliveryStatus.badgeClass(sale.delivery_status)} icon={deliveryStatus.icon(sale.delivery_status)}>{sale.delivery_status}</Badge>
              </div>

              {/* Expanded Detail */}
              {isExpanded && (
                <div className="border-t border-background-100 px-3.5 py-3 space-y-3 animate-slide-up">
                  {/* ---- Ürünler (Kart) ---- */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-shopping-bag-3-line text-foreground-400 text-sm"></i>
                      <span className="text-xs font-semibold text-foreground-700">Satılan Ürünler ({items.length})</span>
                    </div>
                    {items.length > 0 ? (
                      <div className="space-y-2">
                        {items.map((item: any) => (
                          <div key={item.id} className="bg-background-100 rounded-lg p-2.5">
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-xs font-semibold text-foreground-800">{item.product_name || '—'}</span>
                              <span className="text-xs font-bold text-foreground-800">{formatTRY(item.total_price)}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                              <div><span className="text-foreground-400">Cins: </span><span className="text-foreground-600">{item.product_type || '—'}</span></div>
                              <div><span className="text-foreground-400">Marka: </span><span className="text-foreground-600">{item.brand || '—'}</span></div>
                              {item.model_code && <div><span className="text-foreground-400">Model: </span><span className="text-foreground-600">{item.model_code}</span></div>}
                              {item.color && <div><span className="text-foreground-400">Renk: </span><span className="text-foreground-600">{item.color}</span></div>}
                              {item.size && <div><span className="text-foreground-400">Ölçü: </span><span className="text-foreground-600">{item.size}</span></div>}
                              <div><span className="text-foreground-400">Adet: </span><span className="text-foreground-600">{item.quantity}</span></div>
                              <div><span className="text-foreground-400">Birim Fiyat: </span><span className="text-foreground-600">{formatTRY(item.unit_price)}</span></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-foreground-400 italic">Ürün detayı kaydedilmemiş.</p>
                    )}
                  </div>

                  {/* ---- Satış Bilgileri (Kart) ---- */}
                  <div>
                    <span className="text-xs font-semibold text-foreground-700 block mb-2">Satış Bilgileri</span>
                    <div className="bg-background-100 rounded-lg p-3 grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-foreground-400">Tarih: </span><span className="text-foreground-600">{sale.sale_date ? new Date(sale.sale_date).toLocaleDateString('tr-TR') : '—'}</span></div>
                      <div><span className="text-foreground-400">Ödeme Tipi: </span><span className="text-foreground-600">{sale.payment_type || '—'}</span></div>
                      <div><span className="text-foreground-400">Peşinat: </span><span className="text-foreground-600">{formatTRY(sale.down_payment || 0)}</span></div>
                      <div><span className="text-foreground-400">Toplam Ödenen: </span><span className="text-emerald-600 font-medium">{formatTRY(sale.paid_amount || 0)}</span></div>
                      <div className="col-span-2"><span className="text-foreground-400">Kalan Borç: </span>
                        {(sale.remaining_amount || 0) > 0
                          ? <span className="text-red-500 font-medium">{formatTRY(sale.remaining_amount)}</span>
                          : <span className="text-emerald-600">—</span>
                        }
                      </div>
                      {saleDelivery && (
                        <>
                          {saleDelivery.delivery_date && <div><span className="text-foreground-400">Teslimat: </span><span className="text-foreground-600">{new Date(saleDelivery.delivery_date + 'T00:00:00').toLocaleDateString('tr-TR')}</span></div>}
                          {saleDelivery.delivery_person && <div><span className="text-foreground-400">Nakliyeci: </span><span className="text-foreground-600">{saleDelivery.delivery_person}</span></div>}
                        </>
                      )}
                      {sale.warranty_period && <div><span className="text-foreground-400">Garanti: </span><span className="text-foreground-600">{sale.warranty_period}</span></div>}
                    </div>
                  </div>

                  {/* ---- Satış Notu ---- */}
                  {sale.notes && (
                    <div>
                      <span className="text-xs font-semibold text-foreground-700 block mb-1">Satış Notu</span>
                      <p className="text-xs text-foreground-500 bg-background-100 rounded-lg p-2.5">{sale.notes}</p>
                    </div>
                  )}

                  {/* ---- Taksit Planı (Kart) ---- */}
                  {installments.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="text-xs font-semibold text-foreground-700">Ödeme Planı</span>
                        {(installments[0]?.plan_type) && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${installments[0].plan_type === 'custom' ? 'bg-accent-100 text-accent-700' : 'bg-background-200 text-foreground-500'}`}>
                            {installments[0].plan_type === 'custom' ? 'Özel' : 'Eşit'}
                          </span>
                        )}
                        <span className="text-[10px] text-foreground-400 ml-auto">
                          {formatTRY(installments.reduce((s: number, i: any) => s + (i.original_amount || i.amount || 0), 0))}
                        </span>
                      </div>
                      <div className="space-y-1.5">
                        {installments.map((inst: any) => {
                          const instRemaining = inst.remaining_amount !== undefined ? inst.remaining_amount : ((inst.amount || 0) - (inst.paid_amount || 0));
                          const overdue = inst.status !== 'Ödendi' && inst.status !== 'İptal' && inst.due_date && inst.due_date < new Date().toISOString().split('T')[0] && instRemaining > 0;
                          return (
                            <div key={inst.id} className={`bg-background-100 rounded-lg p-2.5 flex items-center justify-between flex-wrap gap-2 ${overdue ? 'ring-1 ring-red-200' : ''}`}>
                              <div className="flex items-center gap-2 flex-wrap min-w-0">
                                <span className="text-xs font-semibold text-foreground-700 w-6 flex-shrink-0">#{inst.installment_no || inst.sequence_no}</span>
                                <span className="text-[11px] text-foreground-400">{inst.due_date ? new Date(inst.due_date).toLocaleDateString('tr-TR') : '—'}</span>
                                <Badge badgeClass={installmentStatus.badgeClass(overdue ? 'Gecikti' : inst.status)} icon={installmentStatus.icon(overdue ? 'Gecikti' : inst.status)}>{overdue ? 'Gecikti' : inst.status}</Badge>
                              </div>
                              <div className="flex items-center gap-3 flex-shrink-0">
                                <span className="text-xs font-medium text-foreground-800">{formatTRY(inst.original_amount || inst.amount)}</span>
                                {inst.paid_amount > 0 && <span className="text-xs text-emerald-600">Ö: {formatTRY(inst.paid_amount)}</span>}
                                {instRemaining > 0 && <span className="text-xs text-red-500">K: {formatTRY(instRemaining)}</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* ---- Detay Linki ---- */}
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate(`/sales/${sale.id}`); }}
                    className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-background-100 text-xs font-medium text-primary-600 hover:bg-background-200 cursor-pointer active:scale-[0.98] transition-all"
                  >
                    <i className="ri-external-link-line"></i>
                    Satış Detayına Git
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}